/**
 * Validation rules for device customization
 * Generic validation functions that work across all device types
 */

import { DeviceTheme } from '../types/theme-types';
import { DeviceLayout } from '../types/layout-types';
import { ComponentStyles } from '../types/component-types';
import { AssetReference } from '../types/asset-types';
import { ValidationResult, ValidationError, ValidationWarning } from '../types/device-config-types';

/**
 * Hex color regex pattern
 */
const HEX_COLOR_REGEX = /^#([0-9A-F]{3}){1,2}$/i;

/**
 * Validate if a string is a valid hex color
 *
 * @param color - Color string to validate
 * @returns True if valid hex color
 */
export function isValidHexColor(color: string): boolean {
  return HEX_COLOR_REGEX.test(color);
}

/**
 * Validate theme configuration
 *
 * @param theme - Theme to validate
 * @returns Validation result
 */
export function validateTheme(theme: DeviceTheme): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Validate all color fields
  const colorFields: Array<[string, string]> = [
    ['colors.primaryBackground', theme.colors.primaryBackground],
    ['colors.secondaryBackground', theme.colors.secondaryBackground],
    ['colors.primaryText', theme.colors.primaryText],
    ['colors.secondaryText', theme.colors.secondaryText],
    ['colors.mutedText', theme.colors.mutedText],
    ['colors.accentPrimary', theme.colors.accentPrimary],
    ['colors.border', theme.colors.border],
    ['colors.statusSuccess', theme.colors.statusSuccess],
    ['colors.statusWarning', theme.colors.statusWarning],
    ['colors.statusError', theme.colors.statusError],
    ['colors.statusInfo', theme.colors.statusInfo],
  ];

  for (const [field, color] of colorFields) {
    if (!isValidHexColor(color)) {
      errors.push({
        field,
        message: `Invalid hex color format: ${color}`,
        code: 'INVALID_COLOR',
      });
    }
  }

  // Validate optional accent secondary color
  if (theme.colors.accentSecondary && !isValidHexColor(theme.colors.accentSecondary)) {
    errors.push({
      field: 'colors.accentSecondary',
      message: `Invalid hex color format: ${theme.colors.accentSecondary}`,
      code: 'INVALID_COLOR',
    });
  }

  // Validate header gradient if enabled
  if (theme.headerGradient?.enabled) {
    if (!isValidHexColor(theme.headerGradient.from)) {
      errors.push({
        field: 'headerGradient.from',
        message: `Invalid gradient start color: ${theme.headerGradient.from}`,
        code: 'INVALID_COLOR',
      });
    }
    if (!isValidHexColor(theme.headerGradient.to)) {
      errors.push({
        field: 'headerGradient.to',
        message: `Invalid gradient end color: ${theme.headerGradient.to}`,
        code: 'INVALID_COLOR',
      });
    }
    if (theme.headerGradient.angle < 0 || theme.headerGradient.angle > 360) {
      errors.push({
        field: 'headerGradient.angle',
        message: `Gradient angle must be between 0-360 degrees: ${theme.headerGradient.angle}`,
        code: 'INVALID_GRADIENT_ANGLE',
      });
    }
  }

  // Validate footer gradient if enabled
  if (theme.footerGradient?.enabled) {
    if (!isValidHexColor(theme.footerGradient.from)) {
      errors.push({
        field: 'footerGradient.from',
        message: `Invalid gradient start color: ${theme.footerGradient.from}`,
        code: 'INVALID_COLOR',
      });
    }
    if (!isValidHexColor(theme.footerGradient.to)) {
      errors.push({
        field: 'footerGradient.to',
        message: `Invalid gradient end color: ${theme.footerGradient.to}`,
        code: 'INVALID_COLOR',
      });
    }
  }

  // Validate shadow blur values
  if (theme.defaultShadow && theme.defaultShadow.blur < 0) {
    errors.push({
      field: 'defaultShadow.blur',
      message: 'Shadow blur cannot be negative',
      code: 'INVALID_SHADOW_BLUR',
    });
  }

  if (theme.activeShadow && theme.activeShadow.blur < 0) {
    errors.push({
      field: 'activeShadow.blur',
      message: 'Shadow blur cannot be negative',
      code: 'INVALID_SHADOW_BLUR',
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate layout configuration
 *
 * @param layout - Layout to validate
 * @returns Validation result
 */
export function validateLayout(layout: DeviceLayout): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Validate header height (50-200px)
  if (layout.header.height < 50 || layout.header.height > 200) {
    errors.push({
      field: 'header.height',
      message: 'Header height must be between 50-200px',
      code: 'INVALID_HEADER_HEIGHT',
    });
  }

  // Validate logo size (width: 50-300px)
  if (layout.header.logoSize.width < 50 || layout.header.logoSize.width > 300) {
    errors.push({
      field: 'header.logoSize.width',
      message: 'Logo width must be between 50-300px',
      code: 'INVALID_LOGO_WIDTH',
    });
  }

  // Validate title font size (16-60px)
  if (layout.content.titleTypography.fontSize < 16 || layout.content.titleTypography.fontSize > 60) {
    errors.push({
      field: 'content.titleTypography.fontSize',
      message: 'Title font size must be between 16-60px',
      code: 'INVALID_FONT_SIZE',
    });
  }

  // Validate card spacing (0-50px)
  if (layout.card.spacing < 0 || layout.card.spacing > 50) {
    errors.push({
      field: 'card.spacing',
      message: 'Card spacing must be between 0-50px',
      code: 'INVALID_CARD_SPACING',
    });
  }

  // Validate card min height
  if (layout.card.minHeight < 0) {
    errors.push({
      field: 'card.minHeight',
      message: 'Card minimum height cannot be negative',
      code: 'INVALID_CARD_HEIGHT',
    });
  }

  // Validate footer height
  if (layout.footer.height < 0 || layout.footer.height > 150) {
    errors.push({
      field: 'footer.height',
      message: 'Footer height must be between 0-150px',
      code: 'INVALID_FOOTER_HEIGHT',
    });
  }

  // Warnings for extreme values
  if (layout.card.spacing > 30) {
    warnings.push({
      field: 'card.spacing',
      message: 'Large card spacing may cause layout issues on smaller screens',
      code: 'LARGE_CARD_SPACING',
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate component styles configuration
 *
 * @param components - Component styles to validate
 * @returns Validation result
 */
export function validateComponentStyles(components: ComponentStyles): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Validate button border radius (0-50px)
  if (components.button.border.radius < 0 || components.button.border.radius > 50) {
    errors.push({
      field: 'button.border.radius',
      message: 'Button border radius must be between 0-50px',
      code: 'INVALID_BORDER_RADIUS',
    });
  }

  // Validate card border radius (0-50px)
  if (components.card.border.radius < 0 || components.card.border.radius > 50) {
    errors.push({
      field: 'card.border.radius',
      message: 'Card border radius must be between 0-50px',
      code: 'INVALID_BORDER_RADIUS',
    });
  }

  // Validate border colors
  if (!isValidHexColor(components.button.border.color)) {
    errors.push({
      field: 'button.border.color',
      message: `Invalid border color: ${components.button.border.color}`,
      code: 'INVALID_COLOR',
    });
  }

  if (!isValidHexColor(components.card.border.color)) {
    errors.push({
      field: 'card.border.color',
      message: `Invalid border color: ${components.card.border.color}`,
      code: 'INVALID_COLOR',
    });
  }

  // Validate icon opacity (0-1)
  if (components.icon.opacity < 0 || components.icon.opacity > 1) {
    errors.push({
      field: 'icon.opacity',
      message: 'Icon opacity must be between 0-1',
      code: 'INVALID_OPACITY',
    });
  }

  // Validate transition duration (0-1000ms)
  if (components.card.transitionDuration < 0 || components.card.transitionDuration > 1000) {
    errors.push({
      field: 'card.transitionDuration',
      message: 'Transition duration must be between 0-1000ms',
      code: 'INVALID_TRANSITION',
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate icons configuration
 * Checks that required icons exist and have valid asset references
 *
 * @param icons - Icons map to validate
 * @param requiredIcons - Array of required icon keys
 * @returns Validation result
 */
export function validateIcons(
  icons: Record<string, AssetReference>,
  requiredIcons: string[]
): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Check for required icons
  for (const iconKey of requiredIcons) {
    if (!icons[iconKey]) {
      errors.push({
        field: `icons.${iconKey}`,
        message: `Required icon missing: ${iconKey}`,
        code: 'MISSING_ICON',
      });
      continue;
    }

    const icon = icons[iconKey];

    // Validate asset reference
    if (!icon.assetId || !icon.s3Url) {
      errors.push({
        field: `icons.${iconKey}`,
        message: `Invalid asset reference for ${iconKey}`,
        code: 'INVALID_ASSET_REFERENCE',
      });
    }

    // Check for fallback URL
    if (!icon.fallbackUrl) {
      warnings.push({
        field: `icons.${iconKey}`,
        message: `No fallback URL provided for ${iconKey}`,
        code: 'MISSING_FALLBACK',
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate entire configuration
 * Combines all validation rules
 *
 * @param config - Full configuration object
 * @param requiredIcons - Array of required icon keys
 * @returns Combined validation result
 */
export function validateConfiguration(
  config: {
    theme: DeviceTheme;
    layout: DeviceLayout;
    components: ComponentStyles;
    icons: Record<string, AssetReference>;
  },
  requiredIcons: string[]
): ValidationResult {
  const themeResult = validateTheme(config.theme);
  const layoutResult = validateLayout(config.layout);
  const componentResult = validateComponentStyles(config.components);
  const iconResult = validateIcons(config.icons, requiredIcons);

  return {
    valid:
      themeResult.valid && layoutResult.valid && componentResult.valid && iconResult.valid,
    errors: [
      ...themeResult.errors,
      ...layoutResult.errors,
      ...componentResult.errors,
      ...iconResult.errors,
    ],
    warnings: [
      ...themeResult.warnings,
      ...layoutResult.warnings,
      ...componentResult.warnings,
      ...iconResult.warnings,
    ],
  };
}
